import CommonUser from "@/common/models/User";
import Account from "@/inside/models/Account/Account";
import { DateTime, Duration } from "luxon";

/**
 * @property {Account|null} account
 * @property {string|null} email
 * @property {string|null} firstname
 * @property {string|null} function
 * @property {string|null} id
 * @property {string|null} userLanguage
 * @property {string|null} lastname
 * @property {string|null} inviteDateTime
 * @property {string} inviteStatus
 * @property {boolean} canAccessDocumentation
 * @property {string|null} phoneNumber.landline
 * @property {string|null} phoneNumber.mobile
 * @property {boolean} canAccessBeta
 */
export default class User extends CommonUser {
    constructor() {
        super();
        this.account = null;
        this.email = null;
        this.firstname = null;
        this.function = null;
        this.id = null;
        this.userLanguage = null;
        this.lastname = null;
        this.inviteDateTime = null;
        this.inviteStatus = User.inviteStatusComplete;
        this.canAccessDocumentation = true;
        this.phoneNumber = {
            landline: null,
            mobile: null
        };
        this.canAccessBeta = false;
    }

    /**
     * Returns a new `User` instance hydrated with data from given `api_resource`.
     *
     * @param {object} api_resource
     * @return {User}
     */
    static fromAPI(api_resource) {
        const out = new User();
        return Object.assign(out, {
            account: (api_resource.account !== null
                ? Object.freeze(Account.fromAPI(api_resource.account))
                : null
            ),
            email: api_resource.email,
            firstname: api_resource.firstname,
            function: api_resource.function,
            id: api_resource.id,
            userLanguage: api_resource.language,
            lastname: api_resource.lastname,
            phoneNumber: Object.assign(out.phoneNumber, api_resource.phoneNumber),
            inviteDateTime: api_resource.inviteDateTime !== null ? (DateTime.fromFormat(api_resource.inviteDateTime, "yyyy-MM-dd HH:mm:ss")) : null,
            inviteStatus: api_resource.inviteStatus,
            canAccessDocumentation: api_resource.canAccessDocumentation,
            canAccessBeta: api_resource.canAccessBeta
        });
    }

    /**
     * @return {string}
     */
    get fullName() {
        const firstname = typeof this.firstname === "string" ? this.firstname : "";
        const lastname = typeof this.lastname === "string" ? this.lastname : "";
        return `${firstname} ${lastname}`.trim();
    }

    /**
     * Calculates if the invitation was sent to the user more than three days ago.
     *
     * @return {boolean}
     */
    get isExpired() {
        return ((DateTime.now() - this.inviteDateTime) >= User.inviteExpirationDuration);
    }

    /**
     * @return {null|string}
     */
    get language() {
        const out = this.userLanguage ? this.userLanguage : this.account.language;
        return User.allowedLanguages.includes(out) ? out : "en";
    }

    // noinspection JSUnusedGlobalSymbols
    toJSON() {
        return {
            id: this.id,
            email: this.email,
            function: this.function,
            language: this.userLanguage,
            lastname: this.lastname,
            firstname: this.firstname,
            phoneNumber: this.phoneNumber,
            inviteDateTime: this.inviteDateTime,
            inviteStatus: this.inviteStatus,
            canAccessDocumentation: this.canAccessDocumentation,
            canAccessBeta: this.canAccessBeta
        };
    }

    /**
     * @return {string}
     */
    static get inviteStatusComplete() {
        return "complete";
    }

    /**
     * @return {string}
     */
    static get inviteStatusPending() {
        return "pending";
    }

    /**
     * @return {array}
     */
    static get allowedLanguages() {
        return ["fr", "en", "de"];
    }

    /**
     * @return {Duration}
     */
    static get inviteExpirationDuration() {
        // noinspection JSCheckFunctionSignatures
        return Duration.fromISO('PT72H');
    }
}
